import { css } from '@emotion/react'

import { match, P } from 'ts-pattern'
import { ButtonVariant } from 'components/common/Button/ButtonVariant'
import { unifiedColors } from 'styles/colors'
import { media } from 'styles/responsive'
import { styled } from 'styles/styled'
import { theme, themeUtils } from 'styles/theme'

interface InjectedProps {
  $variant?: ButtonVariant
  $fullWidth?: boolean
  $fullWidthOnMobile?: boolean
  $isLogo?: boolean
  $largeIcon?: boolean
  $noBackground?: boolean
  $noBorder?: boolean
  $height?: number
  $padding?: number
  $size?: 'small' | 'medium'
  $hasIcon?: boolean
  $disabled?: boolean
}

export const PopoverContainer = styled.div`
  position: relative;
`

export const StyledButton = styled.button<InjectedProps>`
  padding: 0 16px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 12px;
  border: none;
  height: 44px;
  ${themeUtils.label}
  background-color: ${theme.palette.label.default};
  color: ${theme.palette.surface.subdued};
  transform: translate(0, 0);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  &:active {
    transform: translate(2px, 2px);
    box-shadow: none;
  }
  ${({ $padding }) => css`
    ${$padding && `padding: ${$padding}px;`}
  `}
  ${({ $hasIcon }) =>
    $hasIcon &&
    css`
      padding: 0 12px;
    `}
  ${({ $height }) => {
    if ($height)
      return css`
        height: ${$height}px;
      `
  }}
  ${({ $fullWidth }) => css`
    width: ${$fullWidth ? '100%' : 'fit-content'};
  `}
  ${({ $fullWidthOnMobile }) =>
    $fullWidthOnMobile &&
    css`
      ${media.maxWidth.md} {
        width: 100%;
      }
    `}
  ${({ $isLogo }) => css`
    ${$isLogo && 'padding: 4px; height: 34px; width: 34px;'}
  `}
  ${({ $largeIcon }) =>
    $largeIcon
      ? css`
          width: 72px;
          height: 45px;
        `
      : ''}
 
  ${({ $variant }) => {
    switch ($variant) {
      case ButtonVariant.Primary:
        return css`
          background-color: ${unifiedColors.button.primary.default};
          box-shadow: 1px 2px 0px 0px ${theme.palette.shadow.primary};
          &:hover {
            background-color: ${unifiedColors.button.primary.hovered};
          }
          svg path {
            stroke: ${theme.palette.surface.subdued};
          }
        `
      case ButtonVariant.Secondary:
        return css`
          background-color: ${unifiedColors.button.secondary.default};
          color: ${theme.palette.label.default};
          box-shadow: 1px 2px 0px 0px ${theme.palette.shadow.secondary};
          &:hover {
            background-color: ${unifiedColors.button.secondary.hovered};
          }
          svg path {
            stroke: ${theme.palette.label.default};
          }
        `
      case ButtonVariant.Tertiary:
        return css`
          color: ${unifiedColors.label.onPurple};
          background-color: ${unifiedColors.button.tertiary.default};
          box-shadow: 1px 2px 0px 0px ${theme.palette.shadow.tertiary};
          &:hover {
            background-color: ${unifiedColors.button.tertiary.hovered};
          }
        `
      case ButtonVariant.Refusal:
      case ButtonVariant.Error:
        return css`
          color: ${theme.palette.label.defaultDark};
          background-color: ${theme.palette.button.error.default};
          box-shadow: 1px 2px 0px 0px ${unifiedColors.shadow.error};
          &:hover {
            background-color: ${theme.palette.button.error.hovered};
          }
          svg {
            path {
              stroke: ${theme.palette.label.defaultDark};
            }
          }
        `
      case ButtonVariant.Yellow:
        return css`
          background-color: ${theme.palette.button.quinary.default};
          color: ${theme.palette.label.default};
          box-shadow: 1px 2px 0px 0px ${unifiedColors.shadow.quinary};
          &:hover {
            background-color: ${theme.palette.button.quinary.hovered};
          }
        `
      case ButtonVariant.NotAllowed:
        return css`
          pointer-events: none;
          color: ${theme.palette.label.default};
          background-color: #ffeded;
          border: 1px solid #ff6666;
        `
      case ButtonVariant.Quaternary:
      default:
        return css`
          color: ${theme.palette.label.default};
          background-color: ${theme.palette.button.quaternary.default};
          box-shadow: 1px 2px 0px 0px ${theme.palette.shadow.quaternary};
          border: 1px solid ${theme.palette.border.default};
          &:hover {
            background-color: ${theme.palette.button.quaternary.hovered};
          }
        `
    }
  }}

  ${({ $size }) =>
    match($size)
      .with(P.nullish, () => null)
      .with(
        'small',
        () => css`
          padding: 6px;
          height: 28px;
          font-size: 12px;
          line-height: 1.3;
          border-radius: 8px;

          svg {
            transform: scale(0.9);
          }
        `
      )
      .with(
        'medium',
        () => css`
          padding: 0 10px;
          height: 38px;
          font-size: 14px;
          line-height: 1.3;
          border-radius: 12px;
        `
      )
      .exhaustive()}

  ${({ $noBackground }) =>
    $noBackground &&
    css`
      box-shadow: none;
      background: none;
      &:active {
        transform: translate(0, 0);
        box-shadow: none;
      }
      &:hover {
        background: none;
      }
    `}
  ${({ $noBorder }) =>
    $noBorder &&
    css`
      border: none;
      &:hover {
        border: none;
      }
    `}
    ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.38;
      pointer-events: none;
      &:active {
        transform: translate(0, 0);
        box-shadow: none;
      }
    `}
`

interface IconContainerProps {
  $size?: 'small' | 'medium'
}

export const IconContainerStart = styled.div<IconContainerProps>`
  margin-right: ${({ $size }) => ($size === 'small' ? '4px' : '8px')};
`

export const IconContainerEnd = styled.div<IconContainerProps>`
  margin-left: ${({ $size }) => ($size === 'small' ? '4px' : '8px')};
`

interface SelectableButtonProps {
  selected?: boolean
}
export const SelectableButton = styled(StyledButton)<SelectableButtonProps>`
  ${({ selected }) =>
    !selected &&
    css`
      :hover {
        background-color: ${theme.palette.surface.secondary};
      }
    `}
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${theme.palette.label.default};
      color: ${theme.palette.surface.subdued};
      :hover {
        background-color: ${theme.palette.label.default};
      }
    `}
`
